import type { PocTableRowCustomEvent } from 'commons/types';
import { GraphqlError } from 'core/utils/backend-service';
import { DeviceTypeDetails } from 'modules/devices/domain/api/device-type.api';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import { useEffect } from 'react';
import { RefreshInterfaceConfigProps } from '../configuration-list.context';
import { useConfigurationListContext } from './use-configuration-list-context.hook';

export interface UseConfigurationListReturn {
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
    configurations: DeviceConfigurationInfo[];
    isLoading: boolean;
    deviceTypeSelected: DeviceTypeDetails | undefined;
    error?: GraphqlError;
    selectedRows: DeviceConfigurationInfo[];
    showOnlyActiveConfigs: boolean;
    setSelectedRows: React.Dispatch<React.SetStateAction<DeviceConfigurationInfo[]>>;
    handleOnSelectRows: (
        configurationId: string,
        event: PocTableRowCustomEvent<boolean>,
        configurations: DeviceConfigurationInfo[] | undefined
    ) => void;
    clearSelectedRows: VoidFunction;
    refreshConfigurationList: (config?: RefreshInterfaceConfigProps) => void;
    setShowOnlyActiveConfig: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useConfigurationList = (): UseConfigurationListReturn => {
    const {
        selectedTab,
        configurations,
        isLoading,
        error,
        selectedRows,
        deviceTypeSelected,
        showOnlyActiveConfigs,
        refreshConfigurationList,
        setSelectedTab,
        setSelectedRows,
        setShowOnlyActiveConfig,
    } = useConfigurationListContext();

    const handleOnSelectRows = (
        configurationId: string,
        event: PocTableRowCustomEvent<boolean>,
        configurationsProp: DeviceConfigurationInfo[] | undefined
    ) => {
        const configuration = configurationsProp!.find((conf) => conf.id === configurationId);
        setSelectedRows((prevSelectedRows) =>
            event.detail
                ? [...prevSelectedRows, configuration!]
                : prevSelectedRows.filter((conf) => conf.id !== configurationId)
        );
    };

    const clearSelectedRows = () => setSelectedRows([]);

    useEffect(() => {
        setSelectedRows([]);
    }, [deviceTypeSelected]);

    const refreshConfiguration = (config?: RefreshInterfaceConfigProps) => {
        setSelectedRows([]);
        refreshConfigurationList(config);
    };

    return {
        selectedTab,
        configurations,
        deviceTypeSelected,
        isLoading,
        error,
        selectedRows,
        showOnlyActiveConfigs,
        setSelectedRows,
        setSelectedTab,
        handleOnSelectRows,
        clearSelectedRows,
        refreshConfigurationList: refreshConfiguration,
        setShowOnlyActiveConfig,
    };
};
